import type { LoaderFunctionArgs } from '@remix-run/cloudflare';
import {
  isRouteErrorResponse,
  json,
  useLoaderData,
  useRouteError,
} from '@remix-run/react';
import type { MetaDescriptor, MetaFunction } from '@remix-run/react';
import { asc, eq } from 'drizzle-orm';
import { ArticleLayout } from '~/components/ArticleLayout';
import type { ArticleWithSlug } from '~/lib/articles';
import { PageEntity, SectionEntity } from '~/db/schema.server';
import { database } from '~/db';

export const meta: MetaFunction<typeof loader> = ({
  data,
}): MetaDescriptor[] => {
  const page = data?.pageModel;

  const images = page
    ? page?.media
        .filter(
          m =>
            // og:image does not support svg
            m.media.url.endsWith('.png') || m.media.url.endsWith('.jpg'),
        )
        .map(image => ({
          property: 'og:image',
          content: image?.media.url,
        }))
    : [];

  return [
    { title: page?.title },
    { name: 'description', content: page?.description },
    { property: 'og:title', content: page?.title },
    { property: 'og:type', content: 'article' },
    { property: 'og:url', content: `${page?.slug}` },
    ...images,
    { property: 'og:description', content: page?.description },
    { property: 'og:site_name', content: 'unknownkind' },
  ];
};

export async function loader({ params, context }: LoaderFunctionArgs) {
  const db = database(context.env.DB);

  if (!params.slug) {
    // Should in theory never happen once articles._index has been implemented
    throw new Response('Oh no! Something went wrong!', {
      status: 500,
    });
  }

  const pageModel = await db.query.PageEntity.findFirst({
    where: eq(PageEntity.slug, params.slug),
    with: {
      sections: {
        orderBy: [asc(SectionEntity.order)],
      },
      authors: {
        with: {
          author: true,
        },
      },
      media: {
        with: {
          media: true,
        },
      },
    },
  });

  if (!pageModel) {
    throw new Response('Article not found', { status: 404 });
  }

  return json(
    {
      pageModel,
    },
    // {
    //   headers: {
    //     'Cache-Control': 'public, max-age=3600',
    //   },
    // },
  );
}

export default function Route() {
  const { pageModel } = useLoaderData<typeof loader>();

  return (
    <ArticleLayout
      article={pageModel as unknown as ArticleWithSlug}
      sections={pageModel.sections}
      media={pageModel.media.map(m => m.media)}
      meta={pageModel.meta}
    ></ArticleLayout>
  );
}

export function ErrorBoundary() {
  const error = useRouteError();

  if (isRouteErrorResponse(error)) {
    return (
      <div>
        <h1>
          {error.status} {error.statusText}
        </h1>
        <p>{error.data}</p>
      </div>
    );
  }

  if (error instanceof Error) {
    return (
      <div>
        <h1>Error</h1>
        <p>{error.message}</p>
        <p>The stack trace is:</p>
        <pre>{error.stack}</pre>
      </div>
    );
  }

  return <h1>Unknown Error</h1>;
}
